import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  GraduationCap,
  Layers,
  Plus,
  RefreshCw,
  Sparkles,
  UserPlus,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  DataTableEmpty,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import type { Section, Subject } from "@eduviaos/shared";
import { useSections, useSubjects } from "@/hooks/useAcademicCatalogue";

/**
 * Phase H4 FE-1 — IA Catalogue → Subject coverage matrix.
 *
 * Why a coverage matrix instead of a per-teacher eligibility matrix:
 * the BE today only exposes eligibility through the staff-invite flow.
 * A live per-teacher matrix needs a `GET /api/academic/teacher-
 * eligibilities` endpoint that joins `TeacherEligibility` against the
 * membership document — that's a Phase H5 spike.
 *
 * What this page DOES give the IA today:
 *   - One row per subject, one column per configured grade. The cell
 *     shows whether the subject is applicable to that grade and
 *     whether the grade has at least one section live.
 *   - A clear deep link to the Staff invites flow when the IA wants
 *     to assign teachers to a coverage cell — eligibility is
 *     authored there.
 *
 * The matrix never displays a "live count of teachers per cell"
 * because that field doesn't exist on the BE yet. We surface the
 * limitation explicitly so the IA isn't waiting for data that won't
 * arrive without a BE change.
 */

const QUERY_KEY = "eligibility-matrix";

export function EligibilityMatrixPage() {
  const subjectsQuery = useSubjects();
  const sectionsQuery = useSections();

  // Memoise the derived arrays so the downstream useMemo's that key off
  // them don't recompute on every render (lint: react-hooks/exhaustive-
  // deps). `data?.items` returns a stable reference per fetch, so the
  // memo only invalidates when the BE round-trip actually changes the
  // payload — exactly the behaviour we want.
  const subjects = React.useMemo(
    () => subjectsQuery.data?.items ?? [],
    [subjectsQuery.data?.items],
  );
  const sections = React.useMemo(
    () => sectionsQuery.data?.items ?? [],
    [sectionsQuery.data?.items],
  );

  const isLoading = subjectsQuery.isLoading || sectionsQuery.isLoading;
  const isFetching = subjectsQuery.isFetching || sectionsQuery.isFetching;
  const isError = subjectsQuery.isError || sectionsQuery.isError;
  const errorMessage = React.useMemo(() => {
    const raw = subjectsQuery.error ?? sectionsQuery.error;
    if (raw instanceof Error && raw.message) return raw.message;
    return null;
  }, [subjectsQuery.error, sectionsQuery.error]);

  // Pull the union of grades referenced anywhere in the catalogue:
  // (a) subjects with `applicable_grades` set, plus (b) sections with
  // a `grade_ref`. Subjects with an empty `applicable_grades` are
  // intentionally excluded from the column derivation — they apply
  // everywhere by definition and would otherwise inflate the column
  // axis with grades nobody has configured.
  const grades = React.useMemo(() => deriveGrades(subjects, sections), [subjects, sections]);

  // Index sections by grade so we can flag "grade has 0 sections"
  // cells as a visible gap rather than a silent placeholder.
  const sectionsByGrade = React.useMemo(() => {
    const map = new Map<string, Section[]>();
    for (const section of sections) {
      const list = map.get(section.grade_ref) ?? [];
      list.push(section);
      map.set(section.grade_ref, list);
    }
    return map;
  }, [sections]);

  const refetchAll = React.useCallback(() => {
    void subjectsQuery.refetch();
    void sectionsQuery.refetch();
  }, [subjectsQuery, sectionsQuery]);

  return (
    <div className="mx-auto max-w-6xl py-8" data-testid={QUERY_KEY}>
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/catalogue">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Catalogue
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Subject coverage matrix</PageHeaderHeading>
            <PageHeaderDescription>
              Which subjects are configured for which grades, and which grades have sections live.
              The timetable solver and eligibility builder both read from this picture.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={refetchAll}
              disabled={isFetching}
              aria-label="Refresh coverage matrix"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            <Button asChild size="sm">
              <Link to="/first-run/invite-staff">
                <UserPlus aria-hidden="true" className="size-4" />
                Invite teachers
              </Link>
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">Heads-up:</strong> per-teacher eligibility (subject +
          grade + section) is authored through the Staff invites flow. Once a teacher accepts the
          invite, their eligibility rows materialise and the timetable solver picks them up
          automatically. A live per-teacher coverage view ships in a later phase — until then, this
          matrix shows what eligibility CAN be granted; teacher-level coverage lives on the Staff
          page.
        </CardContent>
      </Card>

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load the catalogue
            {errorMessage ? `: ${errorMessage}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={refetchAll}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <span className="text-muted-foreground text-xs">
            {subjects.length.toLocaleString()} subject{subjects.length === 1 ? "" : "s"} ·{" "}
            {grades.length.toLocaleString()} grade{grades.length === 1 ? "" : "s"} ·{" "}
            {sections.length.toLocaleString()} section{sections.length === 1 ? "" : "s"}
          </span>
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs">
            <span className="inline-flex items-center gap-1">
              <span className="size-2 rounded-full bg-emerald-500" aria-hidden="true" />
              Applicable
            </span>
            <span className="ml-3 inline-flex items-center gap-1">
              <span className="size-2 rounded-full bg-amber-500" aria-hidden="true" />
              Applicable, no sections
            </span>
            <span className="ml-3 inline-flex items-center gap-1">
              <span className="border-border size-2 rounded-full border" aria-hidden="true" />
              Not applicable
            </span>
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-card sticky left-0 z-10 w-[28%] min-w-[14rem]">
                  Subject
                </TableHead>
                {grades.map((grade) => {
                  const gradeSections = sectionsByGrade.get(grade) ?? [];
                  return (
                    <TableHead key={grade} className="text-center text-xs">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-foreground font-mono text-xs">{grade}</span>
                        <span className="text-muted-foreground text-[10px] tabular-nums">
                          {gradeSections.length} sec
                        </span>
                      </div>
                    </TableHead>
                  );
                })}
                <TableHead className="text-right">Applicable to</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={6} columns={Math.max(grades.length + 2, 3)} />
            ) : (
              <TableBody>
                {subjects.map((subject) => (
                  <SubjectMatrixRow
                    key={subject.id}
                    subject={subject}
                    grades={grades}
                    sectionsByGrade={sectionsByGrade}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && subjects.length === 0 ? (
          <DataTableEmpty
            icon={<GraduationCap aria-hidden="true" />}
            title="No subjects yet"
            description="Add at least one subject before this matrix can render. The eligibility builder picks it up from there."
            action={
              <Button asChild size="sm">
                <Link to="/catalogue/subjects">
                  <Plus aria-hidden="true" className="size-4" />
                  Manage subjects
                </Link>
              </Button>
            }
          />
        ) : null}

        {!isLoading && subjects.length > 0 && grades.length === 0 ? (
          <DataTableEmpty
            icon={<Layers aria-hidden="true" />}
            title="No grade columns yet"
            description="Subjects exist, but none of them list applicable grades and no sections are configured. Edit a subject to set the grades it's offered in, or add sections so the matrix can pivot."
            action={
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/catalogue/subjects">
                    <Plus aria-hidden="true" className="size-4" />
                    Edit subjects
                  </Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/catalogue/sections">
                    <Plus aria-hidden="true" className="size-4" />
                    Add sections
                  </Link>
                </Button>
              </div>
            }
          />
        ) : null}
      </DataTable>

      <Card className="mt-6">
        <CardContent className="flex items-start gap-3 py-4">
          <Sparkles aria-hidden="true" className="text-primary mt-0.5 size-4 shrink-0" />
          <div className="space-y-1.5">
            <p className="text-foreground text-sm font-medium">Ready to assign teachers?</p>
            <p className="text-muted-foreground text-xs leading-snug">
              Open the Staff invites wizard. Each row carries an eligibility editor where you pick
              the (subject, grade, section) combos the teacher is qualified for. Class teachers are
              flagged inline. The wizard refuses to send invites for teachers whose eligibility is
              empty — protecting the timetable solver from rows that would silently disqualify
              themselves later.
            </p>
            <Button asChild size="sm" variant="outline" className="mt-2">
              <Link to="/first-run/invite-staff">
                <UserPlus aria-hidden="true" className="size-4" />
                Open Staff invites
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SubjectMatrixRowProps {
  subject: Subject;
  grades: string[];
  sectionsByGrade: Map<string, Section[]>;
}

function SubjectMatrixRow({ subject, grades, sectionsByGrade }: SubjectMatrixRowProps) {
  // Empty `applicable_grades` means "applicable to every grade" per BE
  // contract — render every cell as applicable so the IA isn't confused
  // by an all-empty row.
  const applicableEverywhere = subject.applicable_grades.length === 0;
  const applicableSet = React.useMemo(
    () => new Set(subject.applicable_grades),
    [subject.applicable_grades],
  );

  return (
    <TableRow data-testid={`matrix-row-${subject.id}`}>
      <TableCell className="bg-card sticky left-0 z-10">
        <div className="flex flex-col">
          <span className="text-foreground text-sm font-medium">{subject.name}</span>
          <code className="text-muted-foreground text-[11px]">{subject.code}</code>
        </div>
      </TableCell>
      {grades.map((grade) => {
        const isApplicable = applicableEverywhere || applicableSet.has(grade);
        const gradeSections = sectionsByGrade.get(grade) ?? [];
        const hasSections = gradeSections.length > 0;
        return (
          <TableCell key={`${subject.id}-${grade}`} className="text-center">
            {isApplicable ? (
              hasSections ? (
                <span
                  aria-label={`Applicable to ${grade}`}
                  className="inline-flex size-6 items-center justify-center rounded-full bg-emerald-500/15"
                >
                  <CheckCircle2
                    aria-hidden="true"
                    className="size-3.5 text-emerald-700 dark:text-emerald-300"
                  />
                </span>
              ) : (
                <span
                  aria-label={`Applicable to ${grade}, no sections configured`}
                  title="Applicable, but no sections live for this grade yet"
                  className="inline-flex size-6 items-center justify-center rounded-full bg-amber-500/15"
                >
                  <AlertCircle
                    aria-hidden="true"
                    className="size-3.5 text-amber-700 dark:text-amber-300"
                  />
                </span>
              )
            ) : (
              <span
                aria-label={`Not applicable to ${grade}`}
                className="text-muted-foreground/60 inline-flex size-6 items-center justify-center rounded-full"
              >
                ·
              </span>
            )}
          </TableCell>
        );
      })}
      <TableCell className="text-right">
        {applicableEverywhere ? (
          <Badge variant="outline" className="text-muted-foreground">
            All grades
          </Badge>
        ) : (
          <span className="text-muted-foreground text-xs">
            {subject.applicable_grades.length} grade
            {subject.applicable_grades.length === 1 ? "" : "s"}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

function deriveGrades(subjects: Subject[], sections: Section[]): string[] {
  const set = new Set<string>();
  for (const subject of subjects) {
    for (const grade of subject.applicable_grades) {
      const trimmed = grade.trim();
      if (trimmed) set.add(trimmed);
    }
  }
  for (const section of sections) {
    const trimmed = section.grade_ref.trim();
    if (trimmed) set.add(trimmed);
  }
  return Array.from(set).sort((a, b) => {
    // Numeric-aware sort: "9" < "10" < "11" < "XII" — pure lexical
    // would put "10" before "9" which is the worst possible default.
    const aNum = Number.parseInt(a, 10);
    const bNum = Number.parseInt(b, 10);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
    if (!Number.isNaN(aNum)) return -1;
    if (!Number.isNaN(bNum)) return 1;
    return a.localeCompare(b);
  });
}
